import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { debounce } from 'lodash-decorators';
import { Confirmation, ConfirmationService, MenuItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { ConfigService } from 'src/app/config/shared/config.service';
import { BetaFeatureToggleEnum } from 'src/app/config/shared/constants/beta-feature-toggle.enum';
import { FormResponseSearchDto } from 'src/app/forms/shared/model/form-response-search.dto';
import { FormTemplate } from 'src/app/forms/shared/model/form-template.model';
import { FormTypeEnum } from 'src/app/forms/shared/model/settings/form-type-enum.model';
import { ProfileMin } from 'src/app/profiles/shared/profile-min.model';
import { InvoiceSearchParams } from 'src/app/revenue/shared/invoice/invoice-search-params.model';
import { InvoiceService } from 'src/app/revenue/shared/invoice/invoice.service';
import { DatagridService, NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { FormBuilderDto } from '../shared/dto/form-builder.dto';
import { FormResponseDto } from '../shared/dto/form-response.dto';
import { FormV2ElementTypeEnum } from '../shared/dto/form-v2-element-type.enum';
import { FormV2ResponseDto } from '../shared/dto/form-v2-response.dto';
import { FormV2TypeEnum } from '../shared/enum/form-v2-type.enum';
import { FormBuilderService } from '../shared/service/form-builder.service';
import { FormResponseV2Service } from '../shared/service/form-response-v2.service';
import { Title } from '@angular/platform-browser';
import { FormResponseSearchResultDto } from '../shared/dto/form-response-search-result.dto';
import { FormSubmissionViewModalComponent } from './form-submission-view-modal/form-submission-view-modal.component';
import { FormV2ElementResponseDto } from '../shared/dto/form-v2-element-response.dto';

@Component({
  selector: 'wl-form-submission-list',
  templateUrl: './form-submission-list.component.html',
  styleUrls: ['./form-submission-list.component.less'],
    standalone: false
})
export class FormSubmissionListComponent
  extends LazySelectableTableComponent<FormResponseSearchDto, FormResponseSearchResultDto>
  implements OnInit, OnDestroy
{
  @ViewChild(FormSubmissionViewModalComponent) private _viewDialog: FormSubmissionViewModalComponent;
  form: FormBuilderDto;
  formTemplate: FormTemplate;
  formTypeName: string;
  rowMenuItems = new Map<number, MenuItem[]>();
  invoiceAmounts = new Map<number, number>();
  showProfileDialog = false;
  responseToLink = '';
  linkingProfile = false;
  showSubmissionModal: boolean = false;
  submissionResponse: FormResponseDto<string>;
  exporting: boolean = false;
  private _onDestroy$ = new Subject<void>();
  constructor(
    datagridService: DatagridService,
    private _route: ActivatedRoute,
    private _formBuilderService: FormBuilderService,
    private _formResponseV2Service: FormResponseV2Service,
    private _translationService: TranslationService,
    private _invoiceService: InvoiceService,
    private _confirmationService: ConfirmationService,
    private _notificationService: NotificationService,
    private _navigationService: NavigationService,
    private _configService: ConfigService,
    private _title: Title
  ) {
    super('Id', datagridService);

    this.searchParams = new FormResponseSearchDto();
  }

  async ngOnInit(): Promise<void> {
    this._route.params
      ?.pipe(
        map((params) => {
          if (params.id) {
            const formId = +params.id;
            this.searchParams.FormId = formId;
            this.fetchForm(formId);
            this.search();
          }
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._onDestroy$.next(null);
  }

  fetchResults(): Promise<PagedResultGeneric<FormResponseSearchResultDto>> {
    return this._formResponseV2Service.search<any>(this.searchParams, true).then((response) => {
      const pageInvoiceNums = [];
      for (const result of response.Result) {
        if (result.InvoiceNum) {
          pageInvoiceNums.push(result.InvoiceNum);
        }
      }
      this._invoiceService.advancedSearch({ InvoiceNums: pageInvoiceNums, PageSize: 0 }).then((invoiceResponse) => {
        invoiceResponse.Result.forEach((invoiceResult) => {
          this.invoiceAmounts.set(invoiceResult.InvoiceNum, invoiceResult.TotalAmount);
        });
      });
      return response;
    });
  }

  exportFormSubmissions() {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this._formResponseV2Service
      .formSubmissionsExport(this.searchParams)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      })
      .finally(() => {
        this.exporting = false;
      });
  }

  actionItemsForFormResponse(responseDto: FormResponseDto<string>): MenuItem[] {
    if (!this.rowMenuItems.has(responseDto.Id)) {
      this.rowMenuItems.set(responseDto.Id, [
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.view-submission'),
          command: () => this.viewFormSubmission(responseDto.UniqueId),
        },
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.delete-submission'),
          command: () => this.deleteFormSubmission(responseDto),
        },
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.merge-profile'),
          command: () => this._navigationService.navigateToMergeProfiles(responseDto.PrimaryProfileId),
          visible: !!responseDto.PrimaryProfileId,
        },
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.link-profile'),
          visible: !responseDto.PrimaryProfileId,
          command: () => {
            this.showProfileDialog = true;
            this.responseToLink = responseDto.UniqueId;
          },
        },
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.create-profile'),
          visible:
            !responseDto.PrimaryProfileId &&
            (this.form.FormType === FormV2TypeEnum.Donation || this.form.FormType === FormV2TypeEnum.GeneralSurvey),
          command: () => {
            this.loading = true;
            this._formResponseV2Service.createProfileFromResponse(responseDto.UniqueId).then(() => {
              this.search().then(() => {
                this._notificationService.translateSuccess('form-builder.form-submissions.create-profile-success');
              });
            });
          },
        },
        {
          label: this._translationService.getInstantTitlecase('form-builder.form-submissions.merge-profile'),
          visible: !responseDto.PrimaryProfileId,
          command: () => {
            this._navigationService.navigateToMergeProfiles(responseDto.PrimaryProfileId);
          },
        },
      ]);
    }
    return this.rowMenuItems.get(responseDto.Id);
  }

  async profileSelected(selectedProfile: ProfileMin) {
    this.linkingProfile = true;
    try {
      await this._formResponseV2Service.linkProfile(selectedProfile.ProfileId, this.responseToLink);
    } catch (err) {
      this._notificationService.httpError(err);
    } finally {
      this.showProfileDialog = false;
      this.linkingProfile = false;
      this.filterChanged();
    }
  }

  private viewFormSubmission(uniqueId: string) {
    this._viewDialog.open(uniqueId, this.form.FormType != 1);
  }

  closeSubmissionModal(bool) {
    this.showSubmissionModal = bool;
  }

  private deleteFormSubmission(formResponse: FormResponseDto<any>) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('form-builder.form-submissions.confirm-delete-message'),
      key: 'deleteFormSubmissionCD',
      accept: () => {
        this.loading = true;
        this._formResponseV2Service
          .delete(formResponse.UniqueId)
          .then(() => {
            this._notificationService.translateSuccess('form-builder.form-submissions.delete-success');
            this.search();
          })
          .catch((error) => {
            this._notificationService.httpError(error);
          })
          .finally(() => (this.loading = false));
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  getResponseName(formResponse: FormResponseSearchResultDto) {
    if (formResponse.OrgProfileResponse) {
      let orgResponse: FormV2ElementResponseDto = JSON.parse(formResponse.OrgProfileResponse);
      if (orgResponse && orgResponse.ProfileData.OrgName) {
        return orgResponse.ProfileData.OrgName;
      }
      if (formResponse.IndProfileResponse) {
        let indResponse: FormV2ElementResponseDto = JSON.parse(formResponse.IndProfileResponse);
        if (formResponse.IndProfileResponse) {
          return `${indResponse.ProfileData.FirstName} ${indResponse.ProfileData.LastName}`;
        }
      }
    }
    // not a v2 form. can join form ever NOT have a profile tied to id? The answer is no.
  }

  @debounce(500)
  filterChanged() {
    this.search();
  }

  private fetchForm(id: number): void {
    this._formBuilderService.get(id).then((form) => {
      if (form.Dto) {
        this.formTemplate = JSON.parse(form.Dto);
        if (this.formTemplate.Title) {
          this._title.setTitle(this.formTemplate.Title + ' ' + this._title.getTitle());
        }
      }
      switch (form.FormType) {
        case FormTypeEnum.JoinForm:
          this.formTypeName = '(Join Form)';
          break;
        default:
          this.formTypeName = '';
      }
      this.form = form;
    });
  }
}
